import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { useFlexESimulation } from '@/hooks/useFlexESimulation';
import { Skeleton } from '@/components/ui/skeleton';
import { Info } from 'lucide-react';
import {
  Tooltip as ChartTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';


interface FlexEResultsChartsProps {
  simId: string;
}

export default function FlexEResultsCharts({ simId }: FlexEResultsChartsProps) {
  const { simulation, results, isLoading } = useFlexESimulation(simId);

  if (isLoading || !simulation || !results) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-96 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const battKwh = simulation.batt_kwh || 0;
  const battKw = simulation.batt_kw || 0;

  // Format data for charts
  const chartData = results.map(r => ({
    date: new Date(r.date).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit' }),
    deficit_kwh: r.deficit_kwh,
    max_deficit_kw: r.max_deficit_kw,
    passed: r.passed
  }));

  const passedDays = chartData.filter(d => d.passed);
  const failedDays = chartData.filter(d => !d.passed);

  // Bereken cumulatieve success rate per dag voor vergelijking
  const comparisonData = results.map((r, index) => {
    const upToNowResults = results.slice(0, index + 1);
    const upToNowBaseline = upToNowResults.filter(day => day.baseline_passed).length;
    const upToNowScenario = upToNowResults.filter(day => day.passed).length;
    
    return {
      date: new Date(r.date).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit' }),
      fullDate: r.date,
      baselineSuccessRate: (upToNowBaseline / upToNowResults.length) * 100,
      scenarioSuccessRate: (upToNowScenario / upToNowResults.length) * 100,
      improvement: ((upToNowScenario - upToNowBaseline) / upToNowResults.length) * 100,
    };
  });

  // Custom tooltip for scatter plot
  const CustomScatterTooltip = ({ active, payload }: any) => {
    if (!active || !payload || payload.length === 0) return null;
    
    const data = payload[0].payload;
    const passed = data.passed;
    
    return (
      <div className="bg-background border rounded-lg shadow-lg p-3">
        <p className="font-semibold mb-1">{data.date}</p>
        <p className="text-sm">
          <span className="text-muted-foreground">Piekvermogen:</span>{' '}
          <span className="font-medium">{data.max_deficit_kw.toFixed(1)} kW</span>
        </p>
        <p className="text-sm">
          <span className="text-muted-foreground">Energiebehoefte:</span>{' '}
          <span className="font-medium">{data.deficit_kwh.toFixed(1)} kWh</span>
        </p>
        <p className={`text-sm font-semibold mt-1 ${passed ? 'text-green-600' : 'text-red-600'}`}>
          {passed ? '✓ Geslaagd' : '✗ Gefaald'}
        </p>
      </div>
    );
  };

  return (
    <TooltipProvider>
      <h2 className="text-2xl font-bold mb-6">Simulatieresultaten</h2>
      <div className="space-y-6">
        {/* Chart 0: Baseline vs Scenario Comparison (only if scenario applied AND baseline data exists) */}
        {simulation.scenario_applied && simulation.baseline_success_rate != null && (
          <Card id="flexe-chart-baseline-scenario">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-lg font-semibold">
                  Baseline vs. Scenario: Cumulatieve Success Rate
                </h3>
                <ChartTooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[380px]">
                    <div className="space-y-2 text-sm">
                      <p className="font-semibold">Wat toont deze grafiek?</p>
                      <p>Vergelijking van success rate <strong>met</strong> en <strong>zonder</strong> scenario over tijd.</p>
                      
                      <p className="font-semibold mt-2">Kleurcodering:</p>
                      <div className="space-y-1 pl-2">
                        <p>🔴 <span className="font-semibold text-red-600">Rood (Baseline)</span></p>
                        <p className="pl-4">Situatie zonder de voorgestelde machines/veranderingen</p>
                        
                        <p className="mt-1">🟢 <span className="font-semibold text-green-600">Groen (Met Scenario)</span></p>
                        <p className="pl-4">Situatie mét de voorgestelde machines/veranderingen</p>
                      </div>
                      
                      <p className="font-semibold mt-2">Groene gebied:</p>
                      <p>Het gevulde gebied tussen de lijnen toont de <strong>directe impact</strong> van uw scenario. Hoe groter dit gebied, hoe effectiever de investering.</p>
                      
                      <p className="font-semibold mt-2">Naar klant communiceren:</p>
                      <p>"Zonder de voorgestelde aanpassingen zou u slechts op {simulation.baseline_success_rate?.toFixed(0)}% van de dagen voldoende capaciteit hebben. Met onze oplossing stijgt dit naar {simulation.success_rate?.toFixed(0)}%."</p>
                    </div>
                  </TooltipContent>
                </ChartTooltip>
              </div>
              
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={comparisonData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis 
                    label={{ value: 'Success Rate (%)', angle: -90, position: 'insideLeft' }}
                    domain={[0, 100]}
                    ticks={[0, 20, 40, 60, 80, 100]}
                  />
                  <RechartsTooltip 
                    formatter={(value: number) => `${value.toFixed(1)}%`}
                    labelFormatter={(label) => `Tot ${label}`}
                  />
                  <Legend 
                    verticalAlign="top"
                    height={36}
                  />
                  
                  {/* Baseline lijn (rood) */}
                  <Line 
                    type="monotone" 
                    dataKey="baselineSuccessRate" 
                    stroke="#ef4444" 
                    name="Baseline (zonder scenario)"
                    strokeWidth={2}
                    dot={false}
                  />
                  
                  {/* Scenario lijn (groen) */}
                  <Line 
                    type="monotone" 
                    dataKey="scenarioSuccessRate" 
                    stroke="#22c55e" 
                    name="Met scenario"
                    strokeWidth={2}
                    dot={false}
                  />
                  
                  {/* Reference line voor 100% */}
                  <ReferenceLine 
                    y={100} 
                    stroke="#9ca3af" 
                    strokeDasharray="3 3"
                    strokeOpacity={0.5}
                    label={{ 
                      value: '100% (ideaal)', 
                      position: 'right',
                      fill: '#9ca3af',
                      fontSize: 11
                    }}
                  />
                </LineChart>
              </ResponsiveContainer>
              
              {/* Impact Summary */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-xs text-red-700 dark:text-red-400 font-medium mb-1">
                    Baseline
                  </p>
                  <p className="text-2xl font-bold text-red-800 dark:text-red-300">
                    {simulation.baseline_success_rate?.toFixed(1)}%
                  </p>
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    {simulation.baseline_passed_days} van {simulation.total_days} dagen
                  </p>
                </div>
                
                <div className="p-4 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg">
                  <p className="text-xs text-green-700 dark:text-green-400 font-medium mb-1">
                    Met Scenario
                  </p>
                  <p className="text-2xl font-bold text-green-800 dark:text-green-300">
                    {simulation.success_rate?.toFixed(1)}%
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                    {simulation.passed_days} van {simulation.total_days} dagen
                  </p>
                </div>
                
                <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-xs text-blue-700 dark:text-blue-400 font-medium mb-1">
                    Verbetering
                  </p>
                  <p className="text-2xl font-bold text-blue-800 dark:text-blue-300">
                    +{((simulation.success_rate || 0) - (simulation.baseline_success_rate || 0)).toFixed(1)}%
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    +{(simulation.passed_days || 0) - (simulation.baseline_passed_days || 0)} extra dagen
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Chart 1: Energy Deficit Bar Chart */}
        <Card id="flexe-chart-energy-deficit">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-lg font-semibold">
                Dagelijkse Energiebehoefte vs. Batterijcapaciteit
              </h3>
              <ChartTooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[380px]">
                  <div className="space-y-2 text-sm">
                    <p className="font-semibold">Wat toont deze grafiek?</p>
                    <p>De hoeveelheid energie (kWh) die de batterij moet leveren om tijdens piekuren onder de <strong>contractlimiet van {simulation.cap_limited_kw} kW</strong> te blijven.</p>
                    
                    <p className="font-semibold mt-2">Praktijkvoorbeeld:</p>
                    <p>Als uw klant normaal 235 kW heeft, maar tussen 16:00-21:00 slechts {simulation.cap_limited_kw} kW mag gebruiken, moet de batterij het verschil compenseren.</p>
                    
                    <p className="font-semibold mt-2">Berekening:</p>
                    <p>Voor elk kwartier waarbij verbruik &gt; {simulation.cap_limited_kw} kW:</p>
                    <code className="block bg-muted px-2 py-1 rounded mt-1">
                      (verbruik - {simulation.cap_limited_kw} kW) × 0,25 uur = kWh tekort
                    </code>
                    <p className="mt-1">Som van alle kwartieren = totale dagbehoefte</p>
                    
                    <p className="font-semibold mt-2 text-destructive">Kritiek!</p>
                    <p>Als de staaf <strong>boven</strong> de stippellijn komt, heeft de batterij <strong>onvoldoende capaciteit</strong>. Dit betekent letterlijk dat de klant <strong>zonder stroom komt te zitten</strong> tijdens piekuren.</p>
                  </div>
                </TooltipContent>
              </ChartTooltip>
            </div>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  tick={{ fontSize: 11 }}
                />
                <YAxis 
                  label={{ value: 'kWh', angle: -90, position: 'insideLeft' }}
                  domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.15)]}
                  padding={{ top: 20 }}
                />
                <RechartsTooltip />
                <Legend />
                <ReferenceLine 
                  y={battKwh} 
                  stroke="#ff6b6b" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  strokeOpacity={0.8}
                  label={{ 
                    value: `Batterijcapaciteit (${battKwh} kWh)`, 
                    position: 'top',
                    fill: '#ff6b6b',
                    fontSize: 12,
                    fontWeight: 600
                  }}
                />
                <Bar 
                  dataKey="deficit_kwh" 
                  fill="hsl(var(--chart-1))" 
                  name="Energiebehoefte (kWh)"
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
          <CardFooter className="justify-center pb-6">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-md">
              <span className="text-xs font-medium text-red-700 dark:text-red-400">Batterijcapaciteit:</span>
              <span className="text-sm font-bold text-red-800 dark:text-red-300">{battKwh} kWh</span>
            </div>
          </CardFooter>
        </Card>

        {/* Chart 2: Power Deficit Line Chart */}
        <Card id="flexe-chart-power-deficit">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-lg font-semibold">
                Maximale Vermogensoverschrijding vs. Batterijvermogen
              </h3>
              <ChartTooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[380px]">
                  <div className="space-y-2 text-sm">
                    <p className="font-semibold">Wat toont deze grafiek?</p>
                    <p>Het maximale <strong>piekvermogen</strong> (kW) dat de batterij moet kunnen leveren per dag.</p>
                    
                    <p className="font-semibold mt-2">Praktijkvoorbeeld:</p>
                    <p>Als het verbruik op één moment 185 kW is, maar de contractlimiet is {simulation.cap_limited_kw} kW, dan moet de batterij op dat moment {185 - simulation.cap_limited_kw} kW kunnen leveren.</p>
                    
                    <p className="font-semibold mt-2">Berekening:</p>
                    <code className="block bg-muted px-2 py-1 rounded mt-1">
                      MAX(verbruik - {simulation.cap_limited_kw} kW) per dag
                    </code>
                    
                    <p className="font-semibold mt-2">Betekenis:</p>
                    <p>Als de lijn onder de stippellijn blijft, kan de batterij het piekvermogen leveren. Anders is de <strong>ontlaadsnelheid van de batterij te laag</strong>.</p>
                    
                    <p className="font-semibold mt-2 text-amber-600">Let op!</p>
                    <p>Zelfs als de batterij genoeg capaciteit (kWh) heeft, kan onvoldoende vermogen (kW) leiden tot uitval.</p>
                  </div>
                </TooltipContent>
              </ChartTooltip>
            </div>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  tick={{ fontSize: 11 }}
                />
                <YAxis 
                  label={{ value: 'kW', angle: -90, position: 'insideLeft' }}
                  domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.15)]}
                  padding={{ top: 20 }}
                />
                <RechartsTooltip />
                <Legend />
                <ReferenceLine 
                  y={battKw} 
                  stroke="#4dabf7" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  strokeOpacity={0.8}
                  label={{ 
                    value: `Batterijvermogen (${battKw} kW)`, 
                    position: 'top',
                    fill: '#4dabf7',
                    fontSize: 12,
                    fontWeight: 600
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="max_deficit_kw" 
                  stroke="hsl(var(--chart-1))" 
                  name="Max Overschrijding (kW)"
                  dot={{ fill: 'hsl(var(--chart-1))' }}
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
          <CardFooter className="justify-center pb-6">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-md">
              <span className="text-xs font-medium text-blue-700 dark:text-blue-400">Batterijvermogen:</span>
              <span className="text-sm font-bold text-blue-800 dark:text-blue-300">{battKw} kW</span>
            </div>
          </CardFooter>
        </Card>

        {/* Chart 3: Scatter Plot Pass/Fail */}
        <Card id="flexe-chart-scatter" className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="text-lg font-semibold">
              Dagresultaten: Vermogen vs. Energie
            </h3>
            <ChartTooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-[380px]">
                <div className="space-y-2 text-sm">
                  <p className="font-semibold">Wat toont deze grafiek?</p>
                  <p>Elk punt = één dag, geplot op basis van zowel <strong>energie (kWh)</strong> als <strong>vermogen (kW)</strong>.</p>
                  
                  <p className="font-semibold mt-2">Kleurcodering:</p>
                  <div className="space-y-1 pl-2">
                    <p>🟢 <span className="font-semibold text-green-600">Groen (Geslaagd)</span></p>
                    <p className="pl-4">Batterij kan zowel het vermogen als de energie leveren. <strong>Geen uitval.</strong></p>
                    
                    <p className="mt-1">🔴 <span className="font-semibold text-red-600">Rood (Gefaald)</span></p>
                    <p className="pl-4">Batterij komt tekort op vermogen OF energie (of beide). <strong>Risico op uitval tijdens piekuren.</strong></p>
                  </div>
                  
                  <p className="font-semibold mt-2">Naar klant toe communiceren:</p>
                  <p>"Deze grafiek laat zien welke dagen kritiek zijn. Rode punten betekenen dat u tijdens piekuren zonder stroom zou komen omdat de batterij niet voldoende is."</p>
                  
                  <p className="font-semibold mt-2">Strategisch inzicht:</p>
                  <p>Clusters rechts-boven: dagen met extreem hoog verbruik. Mogelijk productiepieken of apparatuur die tegelijk draait.</p>
                </div>
              </TooltipContent>
            </ChartTooltip>
          </div>
          <ResponsiveContainer width="100%" height={500}>
            <ScatterChart>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                type="number" 
                dataKey="max_deficit_kw" 
                name="Max Vermogen" 
                unit=" kW"
                label={{ 
                  value: 'Piekvermogen Overschrijding (kW)', 
                  position: 'insideBottom',
                  offset: -10,
                  style: { fontSize: 12, fontWeight: 600 }
                }}
                tick={{ fontSize: 11 }}
              />
              <YAxis 
                type="number" 
                dataKey="deficit_kwh" 
                name="Energie" 
                unit=" kWh"
                domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.15)]}
                padding={{ top: 20 }}
                label={{ 
                  value: 'Totale Energiebehoefte (kWh)', 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { fontSize: 12, fontWeight: 600 }
                }}
                tick={{ fontSize: 11 }}
              />
              <RechartsTooltip content={<CustomScatterTooltip />} />
              <Legend 
                verticalAlign="top" 
                height={36}
                wrapperStyle={{ paddingTop: 10, paddingBottom: 20 }}
              />
              <Scatter 
                name="Geslaagd" 
                data={passedDays} 
                fill="#22c55e"
              />
              <Scatter 
                name="Gefaald" 
                data={failedDays} 
                fill="#ef4444"
              />
            </ScatterChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </TooltipProvider>
  );
}
